'use client';

import React, { forwardRef } from 'react';
import { cn } from '@/lib/cn';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    icon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, error, icon, className = '', id, required, ...props }, ref) => {
        const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
        return (
            <div className="space-y-1.5">
                {label && (
                    <label htmlFor={inputId} className="block text-xs text-zinc-400 font-medium">
                        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
                    </label>
                )}
                <div className="relative">
                    {icon && (
                        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-zinc-500">
                            {icon}
                        </div>
                    )}
                    <input
                        ref={ref}
                        id={inputId}
                        required={required}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? `${inputId}-error` : undefined}
                        className={cn(
                            'w-full bg-bg-surface border rounded-xl px-4 py-3',
                            'text-sm text-white placeholder-zinc-600',
                            'transition-colors duration-150',
                            'focus:outline-none focus:border-border-strong focus:ring-1 focus:ring-zinc-500/30',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            error ? 'border-rose-500/50' : 'border-border-default',
                            icon ? 'pl-10' : '',
                            className,
                        )}
                        {...props}
                    />
                </div>
                {error && (
                    <p id={`${inputId}-error`} className="text-xs text-rose-400" role="alert">{error}</p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';
